/**
 * Episode Sessions View
 *
 * Display session history for a specific episode filtered by current admin user.
 * Shows last 10 sessions with basic info (status, timestamps, goals progress).
 *
 * Story 3.9: Session Persistence & Replay - Admin Sessions List
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  getEpisodeSessions,
  finishSession,
  type AdminSessionItem,
} from '../services/episodeSessionApi';
import { useEpisode } from '../services/episodesApi';
import { formatRelativeTime } from 'utils/dateTime';

export function EpisodeSessionsView() {
  const { episodeId } = useParams<{ episodeId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [limit] = useState(10);
  const [offset] = useState(0);

  // Fetch episode details for breadcrumb
  const {
    data: episode,
    isLoading: isLoadingEpisode,
    error: episodeError,
  } = useEpisode(episodeId!);

  // Fetch sessions list
  const {
    data: sessionsData,
    isLoading: isLoadingSessions,
    error: sessionsError,
    refetch,
  } = useQuery({
    queryKey: ['episodeSessions', episodeId, limit, offset],
    queryFn: () => getEpisodeSessions(episodeId!, limit, offset),
    enabled: !!episodeId,
  });

  // Finish session mutation
  const finishSessionMutation = useMutation({
    mutationFn: finishSession,
    onSuccess: () => {
      toast.success('Session finished successfully');
      // Invalidate sessions query to refresh the list
      queryClient.invalidateQueries({ queryKey: ['episodeSessions', episodeId] });
    },
    onError: (error: Error) => {
      toast.error(`Failed to finish session: ${error.message}`);
    },
  });

  useEffect(() => {
    if (episodeError) {
      toast.error('Failed to load episode details');
    }
    if (sessionsError) {
      toast.error('Failed to load sessions');
    }
  }, [episodeError, sessionsError]);

  const handleSessionClick = (_sessionId: string) => {
    // Placeholder for future session detail view
    toast('Session detail view coming soon!', { icon: 'ℹ️' });
  };

  const handleFinishSession = (e: React.MouseEvent, sessionId: string) => {
    e.stopPropagation(); // Prevent session card click
    if (confirm('Are you sure you want to finish this session? This action cannot be undone.')) {
      finishSessionMutation.mutate(sessionId);
    }
  };

  const handleResumeSession = (e: React.MouseEvent, session: AdminSessionItem) => {
    e.stopPropagation(); // Prevent session card click
    // Navigate to try page with session-centric routing
    // Story 3.9.2: Session ID is the single source of truth (no query params needed)
    navigate(`/sessions/${session.session_id}`);
  };

  const handleRetry = () => {
    refetch();
  };

  // Combined loading state
  if (isLoadingEpisode || isLoadingSessions) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  // Error state
  if (episodeError || sessionsError) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-red-600 text-lg mb-4">Failed to load session data</p>
          <button
            onClick={handleRetry}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Retry
          </button>
          <button
            onClick={() => navigate('/episodes')}
            className="ml-4 px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
          >
            Back to Episodes
          </button>
        </div>
      </div>
    );
  }

  const sessions = sessionsData?.sessions || [];

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Breadcrumb Navigation */}
      <nav className="mb-6 text-sm" aria-label="Breadcrumb">
        <ol className="flex items-center space-x-2">
          <li>
            <Link to="/episodes" className="text-blue-600 hover:text-blue-800">
              Episodes
            </Link>
          </li>
          <li className="text-gray-500">/</li>
          <li>
            <Link to={`/episodes/${episodeId}`} className="text-blue-600 hover:text-blue-800">
              {episode?.name || 'Episode'}
            </Link>
          </li>
          <li className="text-gray-500">/</li>
          <li className="text-gray-700 font-medium">Sessions</li>
        </ol>
      </nav>

      {/* Page Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Session History: {episode?.name}</h1>
        <p className="text-gray-600">
          View your test sessions for this episode. Click on a session to see details (coming soon).
        </p>
      </div>

      {/* Empty State */}
      {sessions.length === 0 ? (
        <div className="bg-white rounded-lg shadow-md p-12 text-center" data-testid="empty-state">
          <div className="text-gray-400 mb-4">
            <svg
              className="mx-auto h-24 w-24"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          </div>
          <p className="text-gray-600 text-lg mb-4">No sessions found for this episode</p>
          <p className="text-gray-500 text-sm mb-6">
            Test the episode to create your first session
          </p>
          <button
            onClick={() => navigate(`/episodes/${episodeId}`)}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
          >
            Back to Episode
          </button>
        </div>
      ) : (
        <>
          {/* Sessions List */}
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                Recent Sessions ({sessionsData?.total || 0})
              </h2>
            </div>
            <div className="divide-y divide-gray-200 px-6">
              {sessions.map((session: AdminSessionItem) => (
                <div
                  key={session.session_id}
                  onClick={() => handleSessionClick(session.session_id)}
                  className="py-4 hover:bg-gray-50 cursor-pointer transition-colors"
                  data-testid={`session-card-${session.session_id}`}
                >
                  <div className="flex items-start justify-between">
                    {/* Left: Session Info */}
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        {/* Session ID (truncated) */}
                        <span className="text-sm font-mono text-gray-600">
                          {session.session_id.substring(0, 8)}...
                        </span>

                        {/* Status Badge */}
                        <span
                          className={`px-2 py-1 text-xs rounded ${
                            session.status === 'active'
                              ? 'bg-blue-100 text-blue-800'
                              : session.status === 'completed'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {session.status === 'active'
                            ? 'Active'
                            : session.status === 'completed'
                              ? 'Completed'
                              : 'Finished'}
                        </span>

                        {/* Goals Progress */}
                        {session.total_goals > 0 && (
                          <span className="text-xs text-gray-600">
                            {session.goals_completed}/{session.total_goals} goals
                          </span>
                        )}
                      </div>

                      {/* Timestamps */}
                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        <span>Started {formatRelativeTime(session.created_at)}</span>
                      </div>
                    </div>

                    {/* Right: Actions */}
                    <div className="flex items-center gap-3">
                      {/* Resume Button (only for active sessions) */}
                      {session.status === 'active' && (
                        <button
                          onClick={(e) => handleResumeSession(e, session)}
                          className="px-3 py-1 text-sm bg-green-600 text-white rounded-md hover:bg-green-700"
                          data-testid={`resume-session-${session.session_id}`}
                        >
                          Resume
                        </button>
                      )}

                      {/* Finish Button (only for active sessions) */}
                      {session.status === 'active' && (
                        <button
                          onClick={(e) => handleFinishSession(e, session.session_id)}
                          disabled={finishSessionMutation.isPending}
                          className="px-3 py-1 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                          data-testid={`finish-session-${session.session_id}`}
                        >
                          {finishSessionMutation.isPending ? 'Finishing...' : 'Finish'}
                        </button>
                      )}

                      {/* View Button (only for completed/finished sessions) */}
                      {(session.status === 'completed' || session.status === 'finished') && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/sessions/${session.session_id}`);
                          }}
                          className="px-3 py-1 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
                          data-testid={`view-session-${session.session_id}`}
                        >
                          View
                        </button>
                      )}

                      {/* Chevron Icon */}
                      <div className="text-gray-400">
                        <svg
                          className="h-5 w-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Pagination Note */}
          {sessionsData && sessionsData.total > sessions.length && (
            <div className="mt-4 text-center text-sm text-gray-500">
              Showing {sessions.length} of {sessionsData.total} sessions
            </div>
          )}
        </>
      )}

      {/* Back Button */}
      <div className="mt-8">
        <button
          onClick={() => navigate('/episodes')}
          className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
        >
          Back to Episodes
        </button>
      </div>
    </div>
  );
}
